import * as p from '@clack/prompts'
import type { InitCommandOptions } from '@core-engine/commands/runtime.js'
import type { BetterstartIntegrationId } from '@integration-engine/types.js'

export const DATABASE_PROVIDERS = ['vercel', 'railway', 'manual'] as const
export type DatabaseProvider = (typeof DATABASE_PROVIDERS)[number]

export const STORAGE_PROVIDERS = ['vercel-blob', 'railway-bucket', 'r2', 'local'] as const
export type StorageProvider = (typeof STORAGE_PROVIDERS)[number]

export const DEPLOY_PROVIDERS = ['vercel', 'railway', 'none'] as const
export type DeployProvider = (typeof DEPLOY_PROVIDERS)[number]

export const RAILWAY_BUCKET_REGIONS = ['sjc', 'iad', 'ams', 'sin'] as const
export type RailwayBucketRegion = (typeof RAILWAY_BUCKET_REGIONS)[number]

const STORAGE_INTEGRATIONS = new Set<StorageProvider>(['vercel-blob', 'railway-bucket', 'r2'])

function assertChoice<T extends string>(
  label: string,
  value: string | undefined,
  choices: readonly T[]
): asserts value is T | undefined {
  if (value && !choices.includes(value as T)) {
    throw new Error(`Invalid ${label} "${value}". Choose one of: ${choices.join(', ')}.`)
  }
}

export function validateInitProviderFlags(options: InitCommandOptions): void {
  assertChoice('--database-provider', options.databaseProvider, DATABASE_PROVIDERS)
  assertChoice('--storage-provider', options.storageProvider, STORAGE_PROVIDERS)
  assertChoice('--deploy-provider', options.deployProvider, DEPLOY_PROVIDERS)
  assertChoice('--railway-bucket-region', options.railwayBucketRegion, RAILWAY_BUCKET_REGIONS)

  if (options.databaseUrl && options.databaseProvider && options.databaseProvider !== 'manual') {
    throw new Error('--database-url can only be used with --database-provider manual.')
  }

  if (options.databasePlan && options.databaseProvider && options.databaseProvider !== 'vercel') {
    throw new Error('--database-plan can only be used with --database-provider vercel.')
  }
}

export function resolveFlagStorageProvider(
  explicitProvider: StorageProvider | undefined,
  integrations: BetterstartIntegrationId[]
): StorageProvider | undefined {
  const inferred = Array.from(
    new Set(
      integrations.filter((integration): integration is StorageProvider =>
        STORAGE_INTEGRATIONS.has(integration as StorageProvider)
      )
    )
  )

  if (inferred.length > 1) {
    throw new Error(`Choose only one storage integration. Received: ${inferred.join(', ')}.`)
  }

  const inferredProvider = inferred[0]
  if (explicitProvider && inferredProvider && explicitProvider !== inferredProvider) {
    throw new Error(
      `--storage-provider ${explicitProvider} conflicts with integration ${inferredProvider}.`
    )
  }

  if (explicitProvider === 'local' && inferredProvider) {
    throw new Error(`--storage-provider local conflicts with integration ${inferredProvider}.`)
  }

  return explicitProvider ?? inferredProvider
}

export function addStorageIntegration(
  integrations: BetterstartIntegrationId[],
  provider: StorageProvider
): BetterstartIntegrationId[] {
  if (provider === 'local' || integrations.includes(provider)) {
    return integrations
  }

  return [...integrations, provider]
}

export function validateResolvedDatabaseProvider(
  provider: DatabaseProvider,
  options: InitCommandOptions
): void {
  if (options.databasePlan && provider !== 'vercel') {
    throw new Error('--database-plan can only be used with --database-provider vercel.')
  }
}

export async function promptDeployProvider(
  initialValue: DeployProvider = 'none'
): Promise<DeployProvider> {
  const provider = await p.select({
    message: 'Deploy this project',
    options: [
      {
        value: 'vercel' as const,
        label: 'Vercel'
      },
      {
        value: 'railway' as const,
        label: 'Railway'
      },
      {
        value: 'none' as const,
        label: 'Not now'
      }
    ],
    initialValue
  })

  if (p.isCancel(provider)) {
    p.cancel('Setup cancelled.')
    process.exit(0)
  }

  return provider
}

export async function promptRailwayBucketRegion(): Promise<RailwayBucketRegion> {
  const region = await p.select({
    message: 'Choose a Railway bucket region',
    options: [
      { value: 'iad' as const, label: 'US East (Virginia)' },
      { value: 'sjc' as const, label: 'US West (California)' },
      { value: 'ams' as const, label: 'Europe (Amsterdam)' },
      { value: 'sin' as const, label: 'Asia Pacific (Singapore)' }
    ],
    initialValue: 'iad' as const
  })

  if (p.isCancel(region)) {
    p.cancel('Setup cancelled.')
    process.exit(0)
  }

  return region
}
